// AI Generated Test
import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { ExporterService } from '@client/app/modules/export/services/exporter.service';
import { HistoryPrepareService } from '@client/app/pages/global/changes-history/dashboard/tables/history-prepare.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'changes-history-dashboard-table-seo',
    standalone: false,
    template: `
        <div class="row">
            <div class="col-48">
                <table class="table table-hover table-fixed">
                    <thead>
                    <tr>
                        <th class="col-8 col-lg-7">
                            {{'Crawled at' | translate}}
                        </th>
                        <th class="text-right col-4 col-lg-8">
                            <span class="visible-print-block">{{'Pages' | translate}}</span>
                            <span class="hidden-print">{{'Pages crawled' | translate}}</span>
                        </th>
                        <th class="text-right col-8 col-lg-9">
                            {{'Domain compliance' | translate}}
                        </th>
                        <th *ngIf="allowVerticalAverage" class="text-right col-8 col-lg-9">
                            {{'Comparison to average' | translate}}
                            <span class="fas fa-question-circle hidden-print"
                                  [triggers]="'focus hover'"
                                  [ngbTooltip]="'The average score for domains within your industry.' | translate"
                                  placement="top-right"
                                  container="body">
                            </span>
                        </th>
                        <th class="text-right col-22 col-lg-16">
                            <span class="visible-print-block">{{'Opportunities' | translate}}</span>
                            <span class="hidden-print">{{'SEO opportunities' | translate}}</span>
                        </th>
                        <th class="text-right col-6 col-lg-7">
                            <span class="visible-print-block">{{'Changes' | translate}}</span>
                            <span class="hidden-print">{{'Changes since previous crawl' | translate}}</span>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr *ngFor="let story of historyOutput; trackBy: trackByIndex">
                        <td class="text-v-middle">
                            <user-date [twoLines]="true"
                                       [date]="story.post_processing_done_at || story.date">
                            </user-date>
                        </td>
                        <td class="text-v-middle text-right">
                            {{story.page_count}}
                        </td>
                        <td class="text-right text-v-middle">
                                <table-css-compliance
                                        class="donut"
                                        *ngIf="story.hasCompliance"
                                        [totalCount]="story.doughnutTotalCount"
                                        [errorCount]="story.error">
                                </table-css-compliance>
                            <span *ngIf="!story.hasCompliance" translate>
                                N/A
                            </span>
                        </td>
                        <td *ngIf="allowVerticalAverage" class="text-right text-v-middle comparisonToAverage">
                            <span *ngIf="story.comparisonToAverageStyle === 'positive'" class="positive">
                                <span class="fas fa-chevron-up"></span> {{story.comparisonToAverage}}
                            </span>
                            <span *ngIf="story.comparisonToAverageStyle === 'negative'" class="negative">
                                <span class="fas fa-chevron-down"></span> {{story.comparisonToAverage}}
                            </span>
                            <span *ngIf="story.comparisonToAverageStyle === 'neutral'">
                                {{story.comparisonToAverage}}
                            </span>
                            <span *ngIf="!story.hasCompliance" translate>
                                N/A
                            </span>
                        </td>
                        <td class="text-v-middle text-right">
                            <span class="outer-r-mini"
                                  *ngFor="let opportunity of seoOpportunityOptions; trackBy: trackByIndex">
                                <span [class]="opportunity.color + ' ' + (['SEO', 'SUMMARY'] | monIcons)">
                                </span>
                                <span>{{story.opportunities[opportunity.attr]}}</span>
                            </span>
                        </td>
                        <td class="text-v-middle text-right"
                            [class]="story.changeValueColorClass">
                            <span [class]="story.changeValueIconClass">
                            </span>
                            {{story.changePercentageValue}}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `,
})
export class SeoComponent implements OnInit, OnDestroy, OnChanges {
    @Input() history: any;
    @Input() domain: any;
    @Input() domainGroup: any;

    historyOutput: any[] = [];
    allowVerticalAverage: boolean = false;
    issuesStack: string[] = [];
    seoOpportunityOptions: any[] = [];

    constructor (
        private activeFeatureService: ActiveFeatureService,
        private exporterService: ExporterService,
        private historyPrepareService: HistoryPrepareService,
    ) {}

    ngOnInit (): void {
        this.exporterService.connectToCSVExporter('seo', () => {
            return this.historyOutput;
        });
    }

    ngOnDestroy (): void {
        this.exporterService.disconnectFromCSVExporter('seo');
    }

    ngOnChanges (): void {
        this.issuesStack = ['seo_errors_alerts_count', 'seo_errors_warnings_count', 'seo_errors_infos_count'];
        this.getSeoOpportunityOptions();

        if (this.history) {
            this.allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');
            const preparedHistory = this.historyPrepareService.prepareSEOHistory(this.history);
            this.historyOutput = safeClone(preparedHistory).sort((a: any, b: any) => b.id - a.id);
        }
    }

    trackByIndex (index: number): number {
        return index;
    }

    private getSeoOpportunityOptions (): void {
        this.seoOpportunityOptions = [
            {
                attr: 'seo_errors_alerts_count',
                color: 'text-danger',
            },
            {
                attr: 'seo_errors_warnings_count',
                color: 'text-warning',
            },
            {
                attr: 'seo_errors_infos_count',
                color: 'text-info',
            },
            {
                attr: 'seo_errors_technical_count',
                color: 'text-primary',
            },
        ];
    }
}
